import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import { LoadingContainer } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { OrganizationApi, OrganizationFeatureApi, UserRole } from "../../../../../../../services/Api/types";
import { OrganizationPreviewRequests } from "../../services/types";
import OrganizationFeatureItem from "../Features/OrganizationFeatureItem";
import OrganizationFeaturesEditModal from "../OrganizationFeaturesEditModal";
import OrganizationProductCategoryChartItem from "../Products/OrganizationProductCategoryChartItem";

interface Props {
	organization: OrganizationApi;
	requests: OrganizationPreviewRequests;
	context: string;
	areAllowed: (contexts: Record<string, UserRole[]>) => boolean;
}

const FeaturesTabPreview = (props: Props) => {
	const [features, setFeatures] = useState<OrganizationFeatureApi[] | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [showFeaturesEditModal, setShowFeaturesEditModal] = useState<boolean>(false);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation("admin");
	useEffect(() => {
		fetchFeatures();
	}, []);

	const fetchFeatures = async () => {
		if (!props.requests.getFeatures) return;
		setLoading(true);
		try {
			const res = await props.requests.getFeatures(props.organization.id);
			setFeatures(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};
	const onUpdateFeatures = async () => {
		setShowFeaturesEditModal(false);
		await fetchFeatures();
	};
	const downloadFeaturesUsage = async () => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		try {
			await adminApi.downloadFeaturesUsage(props.organization.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await fetchFeatures();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	if (loading) return <LoadingContainer />;

	const stats = props.organization.stats;
	return (
		<div className="d-flex flex-column">
			<div className="d-flex flex-column gap-2 mb-4 mt-2">
				<OrganizationProductCategoryChartItem
					value={stats.feature_count}
					title={"Ilość"}
					percentValue={stats.feature_percent}
					potentialValue={stats.feature_potential_count}
					currency={"PLN"}
				/>
				<OrganizationProductCategoryChartItem
					value={stats.feature_points}
					title={"Punkty"}
					percentValue={stats.feature_points_percent}
					potentialValue={stats.feature_potential_points}
					currency={"PLN"}
				/>
			</div>
			{props.areAllowed({
				ADMIN: [],
				PARTNER: [UserRole.ADMIN, UserRole.OFFICE, UserRole.SALE, UserRole.SUPPORT],
				SUPPORT: [],
			}) && (
				<div className="d-flex">
					<button onClick={() => setShowFeaturesEditModal(true)} className={"btn btn-primary flex-grow-1"}>
						Edytuj
					</button>

					<button onClick={downloadFeaturesUsage} className={"btn btn-default ms-2"}>
						Odśwież
					</button>
				</div>
			)}

			{features && features.length > 0 ? (
				<>
					<div className="d-flex flex-column gap-2 mt-4">
						{features.map((feature) => (
							<React.Fragment key={feature.name}>
								<OrganizationFeatureItem feature={feature} />
							</React.Fragment>
						))}
					</div>
				</>
			) : (
				<EmptyData />
			)}

			{showFeaturesEditModal && (
				<OrganizationFeaturesEditModal
					requests={props.requests}
					organization={props.organization}
					isShown={showFeaturesEditModal}
					onHide={() => setShowFeaturesEditModal(false)}
					onSuccess={onUpdateFeatures}
					context={props.context}
					areAllowed={props.areAllowed}
				/>
			)}
		</div>
	);
};

export default FeaturesTabPreview;
